import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux';
import Pagination from '../../../../components/pagination/Pagination.jsx';
import Segmented from "../../../../components/segmented/Segmented.jsx";
import TableWithSkeleton from '../../../../components/table/TableWithSkeleton.jsx';
import { GetWithdrawnewApi, VerifyWithdrawTransactionApi, ResendWithdrawTransactionApi  } from '../../../../api/request/wallet/withdraw.js';
import columns from '../../../../columns/withdrawRequest.jsx';
import Eye from "../../../../assets/Eye.jsx";
import Copy from '../../../../components/copy/Copy.jsx';
import ModalComponent from "../../../../components/modals/ModalComponent.jsx";
import { formatDate } from '../../../../utils/utils.js';
import { useResponsive } from '../../../../hooks/useResponsive.js';
import OTPModal from "../../../../components/modals/OTPModal.jsx";
import Lock from "../../../../assets/auth/Lock.jsx";
import toast from 'react-hot-toast';

const Withdrawtable = ({ isRefresh, setIsRefresh }) => {
    const { isMobile } = useResponsive()
    const { userDetails } = useSelector(state => state.user);
    const [filter, setFilter] = useState('Pending');
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [withdrawdetails, setWithdrawdetails] = useState(null);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [sorter, setSorter] = useState({ SortBy: 'requestDate', SortOrder: '' });
    const [isOTPModalOpen, setIsOTPModalOpen] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpLoading, setOtpLoading] = useState(false);
    const [pendingRequestId, setPendingRequestId] = useState(null);

    const getFilteredColumns = (statusFilter) => {
        if (statusFilter === 'Pending') {
            return columns.filter((column) => column?.dataIndex !== 'actionDate');
        }
        return columns;
    };

    const getAllWithdraw = async () => {
        setIsLoading(true);
        try {
            const params = {
                PageNumber: page,
                PageSize: pageSize,
                FilterText: filter,
                UserId: userDetails?._id,
                ...sorter,
            };

            const { data } = await GetWithdrawnewApi(new URLSearchParams(params).toString());
            if (data?.success) {
                setData(
                    data?.data?.map((item) => {
                        return ({
                            ...item,
                            amount: `$ ${item?.amount}`,
                            balance: `$ ${item?.balance}`,
                            requestDate: formatDate(item?.requestDate) || '--',
                            actionDate: formatDate(item?.actionDate) || '--',
                            actions: (
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setWithdrawdetails(item);
                                            setIsOpenModal(true);
                                        }}
                                    >
                                        <Eye />
                                    </button>
                                    {item?.isOTPVerify === false && (
                                        <button type="button" onClick={() => { setPendingRequestId(item?.requestId); setOtp(""); setIsOTPModalOpen(true); handleOtpResend(item?.requestId); }} title="Verify OTP" aria-label="Verify OTP">
                                            <Lock />
                                        </button>
                                    )}
                                </div>
                            )
                        })
                    })
                );
                setTotalRecord(data?.totalRecords);
                setIsRefresh(false);
            }
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getAllWithdraw();
    }, [page, filter, pageSize, sorter, isRefresh]);

    const handleOtpSubmit = async (otpVal) => {
        try {
            setOtpLoading(true);
            const params = { requestId: pendingRequestId, otp: otpVal, actionType: 1 };
            const { data } = await VerifyWithdrawTransactionApi(params);
            if (data?.success) {
                toast.success(data?.message || 'Withdrawal verified successfully');
                setIsOTPModalOpen(false);
                setOtp("");
                getAllWithdraw();
            } else {
                toast.error(data?.message || 'Failed to verify OTP');
            }
        } catch (err) {
        } finally {
            setOtpLoading(false);
        }
    };

    const handleOtpResend = async (requestIdArg) => {
        try {
            setOtp("");
            const id = requestIdArg ?? pendingRequestId;
            if (!id) {
                toast.error('Missing request id');
                return false;
            }
            const params = { requestId: id, actionType: 1 };
            const { data } = await ResendWithdrawTransactionApi(params);
            if (data?.success) {
                toast.success(data?.message || 'OTP resent');
                return true;
            }
            toast.error(data?.message || 'Failed to resend OTP');
            return false;
        } catch (err) {
            return false;
        }
    };

    const handleTableChange = (pagination, filters, sorter) => {
        if (sorter?.order) {
            setSorter({ SortBy: sorter?.field, SortOrder: sorter?.order });
        } else {
            setSorter({ SortBy: 'requestDate', SortOrder: '' });
        }
    };

    return (
        <div>
            <div className='block md:flex flex-wrap justify-between items-center gap-5 '>
                <Segmented options={['Pending', 'Approved', 'Rejected']}
                    value={filter}
                    setValue={(value) => {
                        setFilter(value);
                        setPage(1);
                    }}
                    block={isMobile}
                />
            </div>

            <div className='mt-6'>
                <div className="border border-gray-200 rounded-xl">
                    <TableWithSkeleton columns={getFilteredColumns(filter)} data={data} loading={isLoading} onChange={handleTableChange} />
                </div>

                <Pagination total={totalRecord} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} />
            </div>

            <ModalComponent
                isOpen={isOpenModal}
                setIsOpen={setIsOpenModal}
                title='Withdraw Information'
                width={1300}
            >
                <div className='p-5'>
                    <div className='grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-10 '>
                        <div>
                            <p className='text-base font-normal'>Wallet Account No.</p>
                            <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.walletAccountNo}</p>
                        </div>
                        <div>
                            <p className='text-base font-normal'>Withdraw Type</p>
                            <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.withdrawType}</p>
                        </div>
                        <div>
                            <p className='text-base font-normal'>Amount</p>
                            <p className='text-xl primary font-semibold mt-2'>$ {withdrawdetails?.amount}</p>
                        </div>
                        <div>
                            <p className='text-base font-normal'>Wallet Balance</p>
                            <p className='text-xl primary font-semibold mt-2'>$ {withdrawdetails?.balance}</p>
                        </div>
                        <div>
                            <p className='text-base font-normal'>Status</p>
                            <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.requestStatus}</p>
                        </div>
                        <div>
                            <p className='text-base font-normal'>Request Date</p>
                            <p className='text-xl primary font-semibold mt-2'>{formatDate(withdrawdetails?.requestDate) || "--"}</p>
                        </div>
                        {withdrawdetails?.requestStatus === "Pending" ? "" : <div>
                            <p className='text-base font-normal'>Action Date</p>
                            <p className='text-xl primary font-semibold mt-2'>{formatDate(withdrawdetails?.actionDate) || "--"}</p>
                        </div>}
                        {withdrawdetails?.bankAccountNumber ? <div>
                            <p className='text-base font-normal'>Bank Account Number</p>
                            <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.bankAccountNumber || "--"}</p>
                        </div> : ""}
                        {withdrawdetails?.bankBranchAddress ? <div>
                            <p className='text-base font-normal'>Bank Branch Address</p>
                            <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.bankBranchAddress || '--'}</p>
                        </div> : ""}
                        {withdrawdetails?.bankName ? <div>
                            <p className='text-base font-normal'>Bank Name</p>
                            <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.bankName || '--'}</p>
                        </div> : ""}
                        {withdrawdetails?.iban ? <div>
                            <p className='text-base font-normal'>IBAN</p>
                            <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.iban || '--'}</p>
                        </div> : ""}
                        {withdrawdetails?.ifscCode ? <div>
                            <p className='text-base font-normal'>IFSC Code</p>
                            <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.ifscCode || '--'}</p>
                        </div> : ""}
                        {withdrawdetails?.swifT_BIC_Code ? <div>
                            <p className='text-base font-normal'>SwifT BIC Code</p>
                            <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.swifT_BIC_Code || '--'}</p>
                        </div> : ""}
                        {withdrawdetails?.cryptoWalletAddress ? <div>
                            <p className='text-base font-normal'>Crypto Wallet Address</p>
                            <div className="flex items-center gap-3">
                                <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.cryptoWalletAddress || '--'}</p>
                                <Copy text={withdrawdetails?.cryptoWalletAddress} />
                            </div>
                        </div> : ""}
                        {withdrawdetails?.paypalEmail ? <div>
                            <p className='text-base font-normal'>Paypal Email</p>
                            <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.paypalEmail || '--'}</p>
                        </div> : ""}
                        {withdrawdetails?.upiId ? <div>
                            <p className='text-base font-normal'>UPI Id</p>
                            <div className="flex items-center gap-3">
                                <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.upiId || '--'}</p>
                                <Copy text={withdrawdetails?.upiId} />
                            </div>
                        </div> : ""}
                        {withdrawdetails?.requestStatus !== "Pending" ? <div className='col-span-full'>
                            <p className='text-base font-normal'>Comment</p>
                            <p className='text-xl primary font-semibold mt-2'>{withdrawdetails?.comment || '--'}</p>
                        </div> : ""}
                    </div>
                </div>

            </ModalComponent>
            <OTPModal
                isOpen={isOTPModalOpen}
                setIsOpen={setIsOTPModalOpen}
                title='OTP Verification'
                message='Please enter the OTP to verify withdrawal request.'
                width={600}
                value={otp}
                onChange={(val) => setOtp(val)}
                onSubmit={handleOtpSubmit}
                onResend={handleOtpResend}
                isLoading={otpLoading}
                loadingLabel='Verifying...'
                submitLabel='Verify'
                otpDuration={120}
            />
        </div>
    )
}

export default Withdrawtable
